import {
    Color3,
    Constants,
    Mesh,
    MeshBuilder,
    TransformNode,
    Vector3,
    type ParticleSystem,
    type Scene,
    type ShaderMaterial,
} from '@babylonjs/core';
import { registerIntercedeShaders } from './intercede-shaders';
import { EssenceThread } from './essence-thread';
import { burstSparksAlong, GlowFlare, warmTetherPieces } from './tether';
import type { VfxWarmup } from './vfx-warmup';
import { smoothstep } from './easing';
import { createVfxShaderMaterial } from './vfx-shader-material';

export interface IntercedeOptions {
    /** The caster's chest, in scene space; `move` keeps it current. */
    readonly casterChest: Vector3;
    /** The ally's chest. */
    readonly allyChest: Vector3;
    readonly durationSeconds: number;
    /** The bond breaks past this distance, in meters. */
    readonly breakRangeMeters: number;
    readonly colorHex: string;
}

/** The tether reaching out from the caster to the ally on cast, under the bond flash. */
const REACH_SECONDS = 0.15;
const BOND_FLASH_SECONDS = 0.4;
const TETHER_WIDTH = 0.5;
/** How far the tether bows upward at its middle (`EssenceThreadOptions.bend`); the pulse bead follows the same arc. */
const TETHER_BEND = 0.1;
/** Brighter than a thread's default, so the gold bond reads from across a fight. */
const TETHER_INTENSITY = 1.5;
/** Past this fraction of the break range the tether starts to thin and flicker. */
const STRAIN_START = 0.6;
/** How much of its strength (strand spread and thickness) and brightness the tether loses at the very edge of its range. */
const STRAIN_THINNING = 0.8;
const STRAIN_DIMMING = 0.35;
/** How often the straining tether's flicker reshuffles, and how much of the time it drops out at the edge of its range. */
const FLICKER_STEPS_PER_SECOND = 15;
const FLICKER_DROPOUT = 0.6;
/** A redirected hit's pulse running down the tether from the ally to the caster: a bright bead along the arc, the thread surging as it passes. */
const PULSE_SECONDS = 0.25;
const PULSE_BEAD_SIZE = 0.8;
const PULSE_BEAD_INTENSITY = 1.6;
const PULSE_SURGE = 0.8;
const HIT_FLASH_SECONDS = 0.25;
const ARRIVAL_FLASH_SECONDS = 0.3;
const FLARE_SIZE = 1.3;
const GLYPH_SIZE = 0.42;
/** Where the shield glyph floats: over the ally's shoulder, above their chest. */
const GLYPH_OFFSET = new Vector3(0.3, 0.55, 0);
const GLYPH_INTENSITY = 0.55;
/** The two halves of a snapped tether whipping back to their ends. */
const RECOIL_SECONDS = 0.18;
const SNAP_SPARKS = 60;
/** The bond fading out when its time is up. */
const FADE_SECONDS = 0.3;
/** Time left after the bond ends or snaps for its sparks to die out before it is cleaned up. */
const LINGER_SECONDS = 0.9;

/** A flash playing on one flare: how long ago it started, how long it lasts, how bright it peaks, and whether a ring spreads. */
interface Flash {
    age: number;
    readonly seconds: number;
    readonly peak: number;
    readonly ring: boolean;
}

interface Redirect {
    age: number;
    readonly onArrive?: () => void;
}

interface Bond {
    readonly options: IntercedeOptions;
    readonly root: TransformNode;
    readonly color: Color3;
    readonly caster: Vector3;
    readonly ally: Vector3;
    readonly tether: EssenceThread;
    recoil: EssenceThread | null;
    readonly bead: GlowFlare;
    /** The straining tether's brightness this flicker step, and which step it was drawn for. */
    flicker: number;
    flickerStep: number;
    readonly glyph: Mesh;
    readonly glyphMaterial: ShaderMaterial;
    readonly casterFlare: GlowFlare;
    readonly allyFlare: GlowFlare;
    casterFlash: Flash | null;
    allyFlash: Flash | null;
    readonly redirects: Redirect[];
    readonly sparks: ParticleSystem[];
    elapsed: number;
    endsAt: number;
    snappedAt: number | null;
    readonly snapPoint: Vector3;
}

/**
 * Draws Intercede (docs/gameplay/combat-vfx.md): a gold flash on caster
 * and ally as a tether reaches from the caster's chest to the ally's. The
 * tether follows both (`move`), thins and flickers as they near the break
 * range, and snaps with sparks past it. A faint shield glyph floats over
 * the ally's shoulder for the duration. When the ally is hit (`redirect`),
 * the hit flashes on them and a gold pulse runs down the tether into the
 * caster, where the damage number belongs.
 *
 * The tether is an `EssenceThread`; flashes, the pulse bead and the snap
 * sparks are the shared tether pieces (`tether.ts`). Sparks and flicker use
 * Babylon particles and `Math.random` - client visuals need no `SeededRng`.
 */
export class IntercedeSystem {
    private readonly bonds = new Map<string, Bond>();
    private timeSeconds = 0;
    private bondCount = 0;

    constructor(private readonly scene: Scene) {
        registerIntercedeShaders();
    }

    /** Readies this system's shaders and textures before its first effect, so it shows at once (`VfxWarmup`). */
    warm(warmup: VfxWarmup): void {
        // The tether is an essence thread, warmed with every other thread.
        warmup.material(this.createGlyphMaterial('intercede-warmup-glyph', Color3.White()));
        warmTetherPieces(this.scene, warmup);
    }

    /** Binds caster and ally, by an id the caller tracks it with; casting again with the same id starts it over. */
    start(id: string, options: IntercedeOptions): void {
        const existing = this.bonds.get(id);
        if (existing) {
            this.disposeBond(existing);
        }
        const name = `intercede-${this.bondCount++}`;
        const root = new TransformNode(name, this.scene);
        const color = Color3.FromHexString(options.colorHex);

        const tether = this.createTether(`${name}-tether`, root, options.colorHex);

        const glyphMaterial = this.createGlyphMaterial(`${name}-glyph`, color);
        const glyph = MeshBuilder.CreatePlane(`${name}-glyph`, { size: GLYPH_SIZE }, this.scene);
        glyph.parent = root;
        glyph.billboardMode = Mesh.BILLBOARDMODE_ALL;
        glyph.material = glyphMaterial;
        glyph.isPickable = false;

        const bondFlash = (): Flash => ({ age: 0, seconds: BOND_FLASH_SECONDS, peak: 1.6, ring: true });
        const bond: Bond = {
            options,
            root,
            color,
            caster: options.casterChest.clone(),
            ally: options.allyChest.clone(),
            tether,
            recoil: null,
            bead: new GlowFlare(`${name}-bead`, this.scene, Color3.Lerp(color, Color3.White(), 0.5), PULSE_BEAD_SIZE, root),
            flicker: 1,
            flickerStep: -1,
            glyph,
            glyphMaterial,
            casterFlare: new GlowFlare(`${name}-caster-flare`, this.scene, color, FLARE_SIZE, root),
            allyFlare: new GlowFlare(`${name}-ally-flare`, this.scene, color, FLARE_SIZE, root),
            casterFlash: bondFlash(),
            allyFlash: bondFlash(),
            redirects: [],
            sparks: [],
            elapsed: 0,
            endsAt: options.durationSeconds,
            snappedAt: null,
            snapPoint: Vector3.Zero(),
        };
        this.bonds.set(id, bond);
        this.applyFrame(bond, 0);
    }

    /** Keeps the tether on both chests; past the break range it snaps. */
    move(id: string, casterChest: Vector3, allyChest: Vector3): void {
        const bond = this.bonds.get(id);
        if (!bond || bond.snappedAt !== null) {
            return;
        }
        bond.caster.copyFrom(casterChest);
        bond.ally.copyFrom(allyChest);
        if (bond.elapsed < bond.endsAt && Vector3.Distance(casterChest, allyChest) > bond.options.breakRangeMeters) {
            this.snap(id);
        }
    }

    /** The ally is hit and the damage goes to the caster: a flash on the ally and a pulse down the tether; `onArrive` fires as it reaches the caster. */
    redirect(id: string, onArrive?: () => void): void {
        const bond = this.bonds.get(id);
        if (!bond || bond.snappedAt !== null || bond.elapsed >= bond.endsAt) {
            onArrive?.();
            return;
        }
        bond.allyFlash = { age: 0, seconds: HIT_FLASH_SECONDS, peak: 1.8, ring: false };
        bond.redirects.push({ age: 0, onArrive });
    }

    /** The bond breaks (out of range, or the server says so): the tether snaps with sparks. */
    snap(id: string): void {
        const bond = this.bonds.get(id);
        if (!bond || bond.snappedAt !== null || bond.elapsed >= bond.endsAt) {
            return;
        }
        bond.snappedAt = bond.elapsed;
        Vector3.CenterToRef(bond.caster, bond.ally, bond.snapPoint);
        bond.recoil = this.createTether(`${bond.root.name}-recoil`, bond.root, bond.options.colorHex);
        bond.bead.set(0);
        bond.sparks.push(burstSparksAlong(`${bond.root.name}-sparks`, this.scene, bond.caster, bond.ally, bond.color, SNAP_SPARKS));
        bond.glyph.setEnabled(false);
        this.settleRedirects(bond);
    }

    /** Intercede was given more time (recast, or an Echo repeat): the bond holds `seconds` from now, unless it already snapped or faded. */
    extend(id: string, seconds: number): void {
        const bond = this.bonds.get(id);
        if (bond && bond.snappedAt === null && bond.elapsed < bond.endsAt) {
            bond.endsAt = bond.elapsed + seconds;
        }
    }

    /** Intercede ends early, quietly: the bond fades. */
    end(id: string): void {
        const bond = this.bonds.get(id);
        if (bond && bond.snappedAt === null) {
            bond.endsAt = Math.min(bond.endsAt, bond.elapsed);
        }
    }

    update(deltaSeconds: number): void {
        this.timeSeconds += deltaSeconds;
        for (const [id, bond] of this.bonds) {
            bond.elapsed += deltaSeconds;
            const doneAt = bond.snappedAt !== null ? bond.snappedAt + LINGER_SECONDS : bond.endsAt + FADE_SECONDS + LINGER_SECONDS;
            if (bond.elapsed >= doneAt) {
                this.disposeBond(bond);
                this.bonds.delete(id);
                continue;
            }
            this.applyFrame(bond, deltaSeconds);
        }
    }

    dispose(): void {
        this.bonds.forEach((bond) => this.disposeBond(bond));
        this.bonds.clear();
    }

    private applyFrame(bond: Bond, deltaSeconds: number): void {
        const { elapsed } = bond;
        this.applyFlash(bond.casterFlare, bond.casterFlash, bond.caster, deltaSeconds);
        this.applyFlash(bond.allyFlare, bond.allyFlash, bond.ally, deltaSeconds);

        if (bond.snappedAt !== null) {
            this.applySnap(bond, deltaSeconds);
            return;
        }

        const fade = 1 - Math.min(1, Math.max(0, (elapsed - bond.endsAt) / FADE_SECONDS));
        const reach = Math.min(1, elapsed / REACH_SECONDS);
        const strain = smoothstep(STRAIN_START, 1, Vector3.Distance(bond.caster, bond.ally) / bond.options.breakRangeMeters);
        const reachedTo = Vector3.Lerp(bond.caster, bond.ally, reach);
        bond.tether.setEnds(bond.caster, reachedTo);
        bond.tether.setStrength(1 - (STRAIN_THINNING * strain));
        bond.tether.mesh.setEnabled(fade > 0);
        bond.tether.update(deltaSeconds);

        // The newest redirected hit's pulse runs from the ally's end (1) to the caster's (0).
        let pulse: number | null = null;
        for (let index = bond.redirects.length - 1; index >= 0; index -= 1) {
            const redirect = bond.redirects[index]!;
            redirect.age += deltaSeconds;
            if (redirect.age >= PULSE_SECONDS) {
                bond.redirects.splice(index, 1);
                bond.casterFlash = { age: 0, seconds: ARRIVAL_FLASH_SECONDS, peak: 1.4, ring: true };
                redirect.onArrive?.();
                continue;
            }
            pulse = 1 - (redirect.age / PULSE_SECONDS);
        }
        if (pulse !== null) {
            // The tether is laid from the caster to the ally, so the bead runs from 1 back to 0 along it.
            bond.bead.mesh.position.copyFrom(bond.tether.pointAt(pulse));
        }
        // Swells up quickly off the ally and stays bright until the caster's flash takes over.
        const pulseGlow = pulse === null ? 0 : Math.min(1, 0.4 + ((1 - pulse) * 4));
        bond.bead.set(PULSE_BEAD_INTENSITY * pulseGlow * fade);
        bond.tether.setIntensity(TETHER_INTENSITY * (1 - (STRAIN_DIMMING * strain)) * this.resolveFlicker(bond, strain) * (1 + (PULSE_SURGE * pulseGlow)) * fade);

        bond.glyph.setEnabled(fade > 0);
        bond.glyph.position.copyFrom(bond.ally).addInPlace(GLYPH_OFFSET);
        bond.glyph.position.y += 0.04 * Math.sin(this.timeSeconds * 2.2);
        const hitBoost = bond.allyFlash && !bond.allyFlash.ring ? 1 - (bond.allyFlash.age / bond.allyFlash.seconds) : 0;
        bond.glyphMaterial.setFloat('intensity', (GLYPH_INTENSITY + Math.max(0, hitBoost)) * Math.min(1, elapsed / BOND_FLASH_SECONDS) * fade);
        bond.glyphMaterial.setFloat('time', this.timeSeconds);
    }

    /** The two halves whip back from the break toward caster and ally; the second half rides on the main tether. */
    private applySnap(bond: Bond, deltaSeconds: number): void {
        const recoil = Math.min(1, (bond.elapsed - bond.snappedAt!) / RECOIL_SECONDS);
        const showing = recoil < 1;
        bond.tether.mesh.setEnabled(showing);
        bond.recoil?.mesh.setEnabled(showing);
        if (!showing) {
            return;
        }
        const casterEnd = Vector3.Lerp(bond.snapPoint, bond.caster, recoil);
        const allyEnd = Vector3.Lerp(bond.snapPoint, bond.ally, recoil);
        for (const [half, end, from] of [[bond.tether, casterEnd, bond.caster], [bond.recoil, allyEnd, bond.ally]] as const) {
            if (!half) {
                continue;
            }
            half.setEnds(from, end);
            half.setStrength(1 - STRAIN_THINNING);
            half.setIntensity(TETHER_INTENSITY * 1.2 * (1 - recoil));
            half.update(deltaSeconds);
        }
    }

    /** The faint shield glyph over the ally's shoulder. */
    private createGlyphMaterial(name: string, color: Color3): ShaderMaterial {
        const material = createVfxShaderMaterial(this.scene, name, 'intercedeGlyph', { uniforms: ['worldViewProjection', 'time', 'intensity', 'glyphColor'], alphaMode: Constants.ALPHA_ADD });
        material.setColor3('glyphColor', color);
        return material;
    }

    private createTether(name: string, parent: TransformNode, colorHex: string): EssenceThread {
        return new EssenceThread(name, this.scene, { parent, colorHex, width: TETHER_WIDTH, bend: TETHER_BEND });
    }

    /** Straining, the tether drops out at random from step to step, more often the nearer it is to breaking. */
    private resolveFlicker(bond: Bond, strain: number): number {
        const step = Math.floor(this.timeSeconds * FLICKER_STEPS_PER_SECOND);
        if (step !== bond.flickerStep) {
            bond.flickerStep = step;
            bond.flicker = Math.random() < strain * FLICKER_DROPOUT ? 0.25 + (Math.random() * 0.35) : 1;
        }
        return strain > 0 ? bond.flicker : 1;
    }

    private applyFlash(flare: GlowFlare, flash: Flash | null, at: Vector3, deltaSeconds: number): void {
        flare.mesh.position.copyFrom(at);
        if (!flash || flash.age >= flash.seconds) {
            flare.set(0);
            return;
        }
        flash.age += deltaSeconds;
        const t = Math.min(1, flash.age / flash.seconds);
        flare.set(flash.peak * (1 - t), flash.ring ? Math.max(0.01, t) : 0);
    }

    /** Pulses still running when the bond goes: their damage still lands on the caster. */
    private settleRedirects(bond: Bond): void {
        bond.redirects.forEach((redirect) => redirect.onArrive?.());
        bond.redirects.length = 0;
    }

    private disposeBond(bond: Bond): void {
        this.settleRedirects(bond);
        // The particle texture is the shared glow halo, so it is kept.
        bond.sparks.forEach((sparks) => sparks.dispose(false));
        bond.tether.dispose();
        bond.recoil?.dispose();
        bond.bead.dispose();
        bond.casterFlare.dispose();
        bond.allyFlare.dispose();
        bond.glyphMaterial.dispose();
        bond.root.dispose();
    }
}

